import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { fetchAirdetectDetectionRemarksBatch } from "../APIClient";
import { useGetOrganizationId } from "../cacheQueries";
import {
  mergeFetchedRemarks,
  normalizeDetectionId,
  parseRemarkStoreKey,
  remarkStoreKey,
  setRemark,
  type DetectionRemarksMap,
  type RemarkRef,
} from "./detectionRemarksKeys";

export {
  REMARKS_MAX_LENGTH,
  remarkRefOf,
  remarkRefToFetch,
  remarkStoreKey,
  type DetectionRemarksMap,
  type RemarkRef,
} from "./detectionRemarksKeys";

const STORE_ROOT = "detectionRemarks";

export function detectionRemarksQueryKey(organizationId: unknown) {
  return [STORE_ROOT, organizationId] as const;
}

// List queries whose rows carry their own `remarks` (MySQL-backed). A resync
// marks them stale so the next mount re-reads them; see refreshDetectionRemarks.
const REMARK_LIST_ROOTS = new Set([
  "scannedDevices",
  "scannedDevicesWindowed",
  "encryptedScannedDevices",
  "todaysEncryptedScannedDevices",
  "recentEncrypted",
]);

const installedClients = new WeakSet<QueryClient>();

// Once per QueryClient: never garbage-collect the store, and drop the
// coalescer state whenever the store itself is dropped.
//
// The store holds SSE and save writes that the list rows don't have, and
// `requestedKeys` below would otherwise outlive it: an evicted store (default
// gcTime is 5 min without observers) came back empty with every key marked as
// already fetched. The defaults cover a store first created by setQueryData
// (an SSE event while the intrusion log isn't mounted), which a useQuery
// option would not. Logout (`queryClient.clear()`), Login's removeQueries and
// the org switch all remove the query, so they all reset through the
// `removed` event.
function installStoreLifecycle(queryClient: QueryClient): void {
  if (installedClients.has(queryClient)) return;
  installedClients.add(queryClient);
  queryClient.setQueryDefaults([STORE_ROOT], { gcTime: Infinity });
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "removed") return;
    const [root, organizationId] = event.query.queryKey;
    if (root !== STORE_ROOT) return;
    if (String(organizationId) === activeOrgKey) resetCoalescer(null);
  });
}

export function useDetectionRemarksStore() {
  const { data: organizationId } = useGetOrganizationId();
  const queryClient = useQueryClient();
  installStoreLifecycle(queryClient);

  // A “store query”: it doesn't fetch by itself, but it provides a stable,
  // subscribable cache entry that SSE + mutations can update.
  return useQuery<DetectionRemarksMap>({
    queryKey: detectionRemarksQueryKey(organizationId),
    queryFn: async () => ({}),
    initialData: {},
    staleTime: Infinity,
    enabled: !!organizationId,
  });
}

const MAX_IDS_PER_BATCH = 500;
const FLUSH_DEBOUNCE_MS = 200;

// MODULE-LEVEL coalescer. Every useEnsureDetectionRemarks call site —
// the page-level list in useIntrusionDevices, the open IntrusionLog
// dialog, anything added later — pushes into the same `pendingKeys` set
// and resets the same `pendingTimer`, so they all share one debounced
// network request instead of each instance firing its own.
//
// Entries are store keys (see remarkStoreKey), so a standard and an
// encrypted detection with the same numeric id are fetched from their
// own tables and never overwrite each other.
//
// `requestedKeys` is the "in-flight or already fetched" dedupe set —
// adding a key here suppresses any future enqueue until:
//   - org changes (whole coalescer state is reset, see below)
//   - the fetch fails (we delete the batch's keys so a future scroll
//     can retry them)
//   - the store query is removed (logout, org switch; see
//     installStoreLifecycle)
//
// `pendingOverwrite` is set by refreshDetectionRemarks: the next batch
// replaces existing store entries instead of deferring to them, except
// entries SSE or a save wrote after the request went out (`lastWriteSeq`).
//
// `generation` changes on every reset, so a response that arrives after a
// logout or org switch is dropped instead of recreating the old store.
//
// Tied to a single org at a time. When the active org flips, we
// drop everything because the new org's queryCache + ids are
// disjoint from the previous one's.
let activeOrgKey: string | null = null;
let generation = 0;
const requestedKeys = new Set<string>();
const pendingKeys = new Set<string>();
let pendingOverwrite = false;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let writeSeq = 0;
const lastWriteSeq = new Map<string, number>();
// Keys of the rows the intrusion log renders right now. A resync re-reads
// these plus the store, not the whole loaded history.
let onScreenKeys: ReadonlySet<string> = new Set();

function resetCoalescer(newOrgKey: string | null): void {
  activeOrgKey = newOrgKey;
  generation += 1;
  requestedKeys.clear();
  pendingKeys.clear();
  pendingOverwrite = false;
  lastWriteSeq.clear();
  if (pendingTimer) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
}

function scheduleFlush(queryClient: QueryClient, organizationId: unknown): void {
  // Extend the single shared timer. Multiple instances all push into the
  // same set; only the last reset wins, so the batch fires once per quiet
  // 200ms window regardless of how many hook instances contributed.
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = setTimeout(() => {
    pendingTimer = null;
    flushPendingBatch(queryClient, organizationId);
  }, FLUSH_DEBOUNCE_MS);
}

function flushPendingBatch(
  queryClient: QueryClient,
  organizationId: unknown,
): void {
  const overwrite = pendingOverwrite;
  pendingOverwrite = false;

  const ids: number[] = [];
  const encryptedIds: number[] = [];
  const taken: string[] = [];
  for (const key of pendingKeys) {
    const ref = parseRemarkStoreKey(key);
    if (!ref) continue;
    const list = ref.encrypted ? encryptedIds : ids;
    if (list.length >= MAX_IDS_PER_BATCH) continue;
    list.push(ref.id);
    taken.push(key);
  }
  for (const key of taken) pendingKeys.delete(key);
  // Anything past the per-table cap stays pending for a follow-up batch.
  if (pendingKeys.size > 0) {
    pendingOverwrite ||= overwrite;
    scheduleFlush(queryClient, organizationId);
  }
  if (taken.length === 0) return;

  const storeKey = detectionRemarksQueryKey(organizationId);
  // The store must exist while the request is out: removing it (logout, org
  // switch) is what moves `generation` on, and a cache cleared before the
  // store was created would otherwise let the response recreate it.
  if (queryClient.getQueryData(storeKey) === undefined) {
    queryClient.setQueryData<DetectionRemarksMap>(storeKey, {});
  }
  const requestGeneration = generation;
  const requestSeq = writeSeq;
  // An SSE event or save that lands while the request is out is newer than
  // the response, so an overwriting batch must leave that key alone.
  const untouchedSinceRequest = (
    rows: ReadonlyArray<{ id: number; remarks: string | null }>,
    encrypted: boolean,
  ) =>
    rows.filter((r) => {
      const id = normalizeDetectionId(r?.id);
      if (id == null) return false;
      return (lastWriteSeq.get(remarkStoreKey({ id, encrypted })) ?? 0) <= requestSeq;
    });

  // skipNull: rows without a remark are left out, and the client fills them
  // in as null, so this reads the same from a server that predates the flag.
  void fetchAirdetectDetectionRemarksBatch(ids, encryptedIds, { skipNull: true })
    .then(({ data, encrypted }) => {
      if (requestGeneration !== generation) return;
      const standardRows = overwrite ? untouchedSinceRequest(data, false) : data;
      const encryptedRows = overwrite ? untouchedSinceRequest(encrypted, true) : encrypted;
      queryClient.setQueryData<DetectionRemarksMap>(storeKey, (old) =>
        mergeFetchedRemarks(
          mergeFetchedRemarks(old, standardRows, false, overwrite),
          encryptedRows,
          true,
          overwrite,
        ),
      );
    })
    .catch(() => {
      if (requestGeneration !== generation) return;
      // Allow retry — clear dedupe so the next scroll/render can
      // enqueue these keys again.
      for (const key of taken) requestedKeys.delete(key);
    });
}

function enterOrg(queryClient: QueryClient, organizationId: unknown): void {
  installStoreLifecycle(queryClient);
  const orgKey = String(organizationId);
  if (orgKey !== activeOrgKey) resetCoalescer(orgKey);
}

/**
 * Queues a batch read for every ref the store doesn't hold yet. The list
 * passes only refs whose row lacks its own remark (see remarkRefToFetch).
 */
export function ensureDetectionRemarks(
  queryClient: QueryClient,
  organizationId: unknown,
  refs: ReadonlyArray<RemarkRef>,
): void {
  if (!organizationId) return;
  if (refs.length === 0) return;
  enterOrg(queryClient, organizationId);

  const store =
    queryClient.getQueryData<DetectionRemarksMap>(
      detectionRemarksQueryKey(organizationId),
    ) ?? {};

  let anyAdded = false;
  for (const ref of refs) {
    const key = remarkStoreKey(ref);
    if (Object.prototype.hasOwnProperty.call(store, key)) continue;
    if (requestedKeys.has(key)) continue;
    pendingKeys.add(key);
    requestedKeys.add(key);
    anyAdded = true;
  }
  if (!anyAdded) return;
  scheduleFlush(queryClient, organizationId);
}

export function useEnsureDetectionRemarks(refs: ReadonlyArray<RemarkRef>) {
  const { data: organizationId } = useGetOrganizationId();
  const queryClient = useQueryClient();

  const uniqueRefs = useMemo(() => {
    const byKey = new Map<string, RemarkRef>();
    for (const ref of refs) byKey.set(remarkStoreKey(ref), ref);
    return Array.from(byKey.values());
  }, [refs]);

  useEffect(() => {
    ensureDetectionRemarks(queryClient, organizationId, uniqueRefs);
    // Intentionally no cleanup — the pending batch is shared across
    // hook instances, so unmounting one shouldn't cancel the others'
    // request.
  }, [uniqueRefs, organizationId, queryClient]);
}

// True while a shown list whose rows carry `remarks` is marked stale, i.e.
// after a resync and until that list is re-read: its rows may hold a remark
// that changed while we were disconnected.
function remarkListsStale(queryClient: QueryClient): boolean {
  return queryClient
    .getQueryCache()
    .findAll({ predicate: (q) => REMARK_LIST_ROOTS.has(String(q.queryKey[0])) })
    .some((q) => q.state.isInvalidated && q.getObserversCount() > 0);
}

/**
 * Records which detections the intrusion log renders (see onScreenKeys).
 * While the lists are stale after a resync, rows that come into view are
 * read as well, since the resync only re-read the rows shown at the time.
 * The store dedupes, so each key is read at most once.
 */
export function setOnScreenDetectionRemarks(
  queryClient: QueryClient,
  organizationId: unknown,
  refs: ReadonlyArray<RemarkRef>,
): void {
  onScreenKeys = new Set(refs.map(remarkStoreKey));
  if (remarkListsStale(queryClient)) {
    ensureDetectionRemarks(queryClient, organizationId, refs);
  }
}

export function useOnScreenDetectionRemarks(refs: ReadonlyArray<RemarkRef>) {
  const { data: organizationId } = useGetOrganizationId();
  const queryClient = useQueryClient();
  useEffect(() => {
    setOnScreenDetectionRemarks(queryClient, organizationId, refs);
    return () => setOnScreenDetectionRemarks(queryClient, organizationId, []);
  }, [refs, organizationId, queryClient]);
}

/**
 * Re-reads the remarks of the rows on screen plus every remark in the store,
 * replacing cached values; a remark the server no longer has is cleared.
 * Called when the server sends `remarks_resync`: our resume point is no
 * longer in its buffer (events aged out, or the api restarted), so updates
 * broadcast while we were disconnected would otherwise never be seen.
 *
 * The lists are marked stale, not refetched, so a deploy doesn't make every
 * client download its whole history at once; they refetch on the next mount.
 * Until then, loaded rows that scroll into view are read as they appear (see
 * setOnScreenDetectionRemarks), and an opened dialog always reads its own.
 */
export function refreshDetectionRemarks(
  queryClient: QueryClient,
  organizationId: unknown,
): void {
  if (!organizationId) return;
  enterOrg(queryClient, organizationId);

  void queryClient.invalidateQueries({
    predicate: (q) => REMARK_LIST_ROOTS.has(String(q.queryKey[0])),
    refetchType: "none",
  });

  const store =
    queryClient.getQueryData<DetectionRemarksMap>(
      detectionRemarksQueryKey(organizationId),
    ) ?? {};
  const keys = new Set([...onScreenKeys, ...Object.keys(store)]);
  if (keys.size === 0) return;
  for (const key of keys) {
    pendingKeys.add(key);
    requestedKeys.add(key);
  }
  pendingOverwrite = true;
  scheduleFlush(queryClient, organizationId);
}

export function upsertDetectionRemark(
  queryClient: QueryClient,
  organizationId: unknown,
  ref: RemarkRef,
  remarks: string | null,
) {
  if (organizationId) enterOrg(queryClient, organizationId);
  lastWriteSeq.set(remarkStoreKey(ref), ++writeSeq);
  queryClient.setQueryData<DetectionRemarksMap>(
    detectionRemarksQueryKey(organizationId),
    (old) => setRemark(old, ref, remarks),
  );
}
